
import { ComboBox, IComboBoxOption } from "@fluentui/react";
import PoolDropdown from "../../jobManualView/PoolDropdown";
import { POOL_ALL_KEY, type SelectedKey } from "../../../helpers/PoolSelectionHelper";
import { JOB_TYPE_LOOKUP } from "../../../../lookup/Consts";
import { CREATE_MANUAL_JOB_TITLE, MANUAL_UNDO_USAGE_INGESTION_JOB_TYPE } from "../../../Consts";
import { JobParameterControlFactory } from "../../../helpers/JobParameterControlFactory";
import { IGenericJobTypeLookupInstance } from "../../../types/IGenericJobTypeLookupInstance";
import { IJobParameterControlBaseDistribution } from "../../../types/JobParameterControls/IJobParameterControlBaseDistribution";
import TextDataInput from "../../../../repertoire/components/textDataInput/TextDataInput";
import React from "react";
import ActionButton from "../../../../repertoire/components/actionButton/ActionButton";
import { getLookupValues, getOptions } from "../../../helpers/UsageJobFormFieldsHelper";
import { formatParams, getFileNameOptions } from "./uuiFormFieldsViewUtils";
import { JOB_CREATED_VIEW } from "../../../../repertoire/Consts";
import { isValidCronExpression } from "../../../../util/cronValidation";
import { IDataSourceBase } from "../../../types/JobParameterControls/IDataSource";
import { ILookupDictionary } from "../../../../lookup/types/ILookupDictionary";
import { IScheduledJobState } from "../../../../redux/types/IScheduledJobState";
import { triggerManualJob } from "../../../../redux/thunks/HomeThunks";
import { showModal } from "../../../../redux/reducers/ModalReducer";
import { MessageBanner, MessageType } from "../../../../repertoire/components/messageBanner/MessageBanner";

export const DISTRIBUTION = "UUI_Distribution";
export const POOL = "UUI_Pool";
export const FILENAME = "UUI_Filename";
const SCHEDULE = "UUI_Schedule"
export const ALL_FILES = "allFiles";

type Props = {
    triggerManualJob: typeof triggerManualJob;
    createNewJob: () => void;
    lookups: ILookupDictionary;
    manualJobDataSources:{  [key: string]: IDataSourceBase };
    getUsageFilenames: (poolValue: string, distributionValue: string) => void;
    getScheduledJob: (jobType: string, parameters: string) => void;
    upsertScheduledJobAsync: (schedule: any) => void;
    scheduledJob?: IScheduledJobState;
    getAllUsageFilenamesFromDistribution: (distributionValue: string) => void;
    usageFileNames?: { data: string[] };
    showModal:  typeof showModal;
    scheduledJobSet: (value: boolean) => void;
    onError?: (errorMessage: string) => void;
}

const UUIFormFieldsView = ({
    triggerManualJob,
    createNewJob,
    lookups,
    manualJobDataSources,
    getUsageFilenames,
    getScheduledJob,
    upsertScheduledJobAsync,
    scheduledJob,
    getAllUsageFilenamesFromDistribution,
    usageFileNames,
    showModal,
    scheduledJobSet,
    onError
}: Props) => {
    const [jobControls, setJobControls] = React.useState([]);
    const [distributionValue, setDistributionValue] = React.useState('');
    const [poolValue, setPoolValue] = React.useState<SelectedKey[]>([]);
    const [poolOptions, setPoolOptions] = React.useState<IComboBoxOption[]>([]);
    const [filenameSelectedKeys, setFilenameSelectedKeys] = React.useState<string[]>([]);
    const [filenameValue, setFileNameValue] = React.useState<string[]>([]);
    const [scheduledValue, setScheduledValue] = React.useState('');
    const [cronError, setCronError] = React.useState('');
    const [scheduleJobSet, setScheduleJobSet] = React.useState(false);
    const [loading, setLoading] = React.useState(false);

    const handleFormatParams = () => {
        return formatParams(jobControls, distributionValue, poolValue, poolOptions, filenameValue)
    }

    React.useEffect(() => {
        const jobTypes = getLookupValues(JOB_TYPE_LOOKUP, lookups, MANUAL_UNDO_USAGE_INGESTION_JOB_TYPE) as IGenericJobTypeLookupInstance;

        if (jobTypes.extraFields.GenericParametersJson && (jobTypes.extraFields.GenericParametersJson != '')) {
            let genericJobBaseParameters = JSON.parse(jobTypes.extraFields.GenericParametersJson) as { controlList: IJobParameterControlBaseDistribution[] }
            let jobControls = JobParameterControlFactory.InstantiateControlList(genericJobBaseParameters.controlList, manualJobDataSources) as IJobParameterControlBaseDistribution[];
            setJobControls(jobControls);
            jobControls.forEach(formItem => {
                if (formItem.ControlID === DISTRIBUTION) {
                    setDistributionValue(formItem.CurrentValue as string)
                }
                if (formItem.ControlID === POOL) {
                    setPoolValue(formItem.CurrentValue ? [...formItem.CurrentValue] : []);
                }
                if (formItem.ControlID === FILENAME) {
                    setFilenameSelectedKeys(formItem.CurrentValue ? [...formItem.CurrentValue] : []);
                }
            })
        }


    }, []);

    React.useEffect(() => {
        if ((poolValue && distributionValue) && (poolValue.length > 0 && distributionValue !== '')) {
            if (poolValue.includes(POOL_ALL_KEY)) {
                getAllUsageFilenamesFromDistribution(distributionValue)
            }
            else {
                poolValue.forEach(pool => {
                    getUsageFilenames(String(pool), distributionValue);
                });
            }
        }
    }, [distributionValue, poolValue]);

    React.useEffect(() => {
        setFileNameValue([]);
        setFilenameSelectedKeys([]);
    }, [poolValue]);

    React.useEffect(() => {
        if (distributionValue && filenameValue) {
            getScheduledJob(MANUAL_UNDO_USAGE_INGESTION_JOB_TYPE, JSON.stringify({ controlList: handleFormatParams() }))
        }
    }, [poolValue, distributionValue, filenameValue]);

    React.useEffect(() => {
        if (scheduledJob) {
            setScheduledValue(scheduledJob.cronSchedule)
        }
    }, [scheduledJob]);

    const handleDistributionChange = (option?: IComboBoxOption) => {
        if (!option) return;

        setDistributionValue(option.key as string);
        
        const poolControl = jobControls.find(c => c.ControlID === POOL);
        let raw = poolControl
        ? ((getOptions(poolControl, option.key as string, true) || []) as IComboBoxOption[])
        : [];
        
        setFileNameValue([]);
        setPoolOptions(raw);
        setPoolValue([]);
        setScheduledValue('')
    }

    const setFileNameValueAndKeys = (value: string[]) => {
        setFileNameValue(value);
        setFilenameSelectedKeys(value);
    }

    const handleFilenameChange = (value: IComboBoxOption) => {
        const selectedKey = value.key as string;

        if (selectedKey === ALL_FILES) {
            if (filenameSelectedKeys.includes(ALL_FILES)) {
                setFileNameValueAndKeys([]);
            } else {
                setFilenameSelectedKeys([ALL_FILES, ...usageFileNames?.data]);
                setFileNameValue([...usageFileNames?.data]);
            }
        } else {
            const filtered = filenameValue.filter(f => f !== ALL_FILES && f !== selectedKey);

            const isAlreadySelected = filenameSelectedKeys.includes(selectedKey);
            if (isAlreadySelected) {
                setFileNameValueAndKeys(filtered);
            } else {
                setFileNameValueAndKeys([...filtered, selectedKey]);
            }
        }

        setScheduledValue('');
    };

    const handleScheduleChange = (value: string | number | boolean, fieldname: string) => {
        setScheduledValue(value as string);
        setCronError('');
    }

    const setParamsAndTriggerManualJob = () => {
        let params = JSON.stringify({ controlList: handleFormatParams() });
        return triggerManualJob({ jobType: MANUAL_UNDO_USAGE_INGESTION_JOB_TYPE, jobParameters: params });
    }

    const getFormComponents = (jobControls) => {
        return jobControls.map(formItem => {

            if (formItem.Type === 'combobox' && formItem.ControlID === DISTRIBUTION) {
                return (
                    <div className="form-group col-md-6 col-sm-6 col-xs-12">
                        <div className="dropdownDataInput">
                            <ComboBox
                                selectedKey={distributionValue}
                                label={formItem.Caption}
                                options={getOptions(formItem, distributionValue)}
                                onChange={(e, selectedOption, i, value) => {
                                    if (selectedOption) // Handle when a custom value is typed
                                        handleDistributionChange(selectedOption);
                                }}
                                useComboBoxAsMenuWidth
                                calloutProps={{ calloutMaxHeight: 270 }}
                                autoComplete="on"
                                allowFreeform={true}

                            />
                        </div>
                    </div>
                );
            }

            if (formItem.Type === 'combobox' && formItem.ControlID === POOL) {

                return (
                    <div className="form-group col-md-6 col-sm-6 col-xs-12">
                        <div className="dropdownDataInput">
                            <div className="dropdownDataInput">
                                <PoolDropdown
                                    data-testid="UUI_Pool"
                                    label={formItem.Caption}
                                    poolOptions={poolOptions}
                                    value={poolValue}
                                    onChange={setPoolValue}
                                    disabled={!distributionValue}
                                />
                            </div>
                        </div>
                    </div>
                );
            }

            if (formItem.Type === 'text-input' && formItem.ControlID === FILENAME) {
                const fileOptions = poolValue ? getFileNameOptions(usageFileNames?.data) : [];
                const noFilesAvailable = fileOptions.length === 1 && fileOptions[0].key === 'no-options';

                return (<div className="form-group col-md-6 col-sm-6 col-xs-12">
                    <div className="dropdownDataInput">
                        <ComboBox
                            text={filenameValue.join(",")}
                            selectedKey={filenameSelectedKeys}
                            label={formItem.Caption}
                            options={ fileOptions}
                            onChange={(_e, selectedOption, i, value) => {
                                if (selectedOption)  // Handle when a custom value is typed
                                    handleFilenameChange(selectedOption);
                            }}
                            useComboBoxAsMenuWidth
                            calloutProps={{ calloutMaxHeight: 270 }}
                            placeholder={"No File Selected."}
                            multiSelect={!noFilesAvailable} 
                            multiSelectDelimiter=","
                            autoComplete="on"
                            allowFreeform={true}
                        />
                    </div>
                </div>
                );
            }
        })
    }
    const setSchedule = async () => {
        setLoading(true);
        const valid = isValidCronExpression(scheduledValue);
        let schedule = {
            cronSchedule: scheduledValue,
            errorList: [],
            lastRunDate: null,
            parameters: JSON.stringify({ controlList: handleFormatParams() }),
            type: MANUAL_UNDO_USAGE_INGESTION_JOB_TYPE
        };

        if (valid && schedule) {
            setCronError('');
            try {
                await upsertScheduledJobAsync(schedule);
                scheduledJobSet(true);
                createNewJob();
            } catch (err: any) {
                onError?.(err?.message || err?.toString() || "An error occurred");
            }
        } else {
            setCronError('Please enter a valid Schedule expression.');
        }
        setLoading(false);
    };

    const onClick = async () => {
        setLoading(true);
        try {
            scheduledJobSet(true);
            createNewJob();
            setParamsAndTriggerManualJob();
        } catch (err: any) {
           onError?.(err?.message || err?.toString() || "An error occurred");
        } finally {
            setLoading(false);
        }
    }

    const isValidJob = () => {
        return (distributionValue && filenameValue.length > 0 && poolValue.length > 0)
    }

    const dismissAlert = () => {
        setCronError('');
    };

    return <>
        <div className="row">
            {jobControls && getFormComponents(jobControls)}
        </div>
        <div className="row">

            <div className="form-group col-md-4 col-sm-6 col-xs-12">
                <TextDataInput
                    label={"Schedule"}
                    fieldName={SCHEDULE}
                    changeData={handleScheduleChange}
                    value={scheduledValue}
                    readOnly={!(isValidJob())}
                    isHidden={false}
                    isMandatory={false}
                />
            </div>
            <div style={{ marginTop: 12 }} className="form-group col-md-2 col-sm-4 col-xs-12">
                <ActionButton buttonText={"Set"} buttonAction={setSchedule} disabled={!(isValidJob()) || loading} />
            </div>
        </div>

        {cronError && cronError !== '' && (
            <div className="alert alert-danger alert-dismissible">
                <span>{cronError}</span>
                <button type="button" className="close" aria-label="Close" onClick={dismissAlert}>
                    <span aria-hidden="true">&times;</span>
                </button>
            </div>
        )}

        <div className="row">
            <div className="form-group col-lg-4 col-md-4 col-sm-6 col-xs-12">
                <ActionButton buttonText={"Cancel"} buttonAction={createNewJob} className={"button btn-defaultSecondary"}/>
            </div>
            <div className="form-group col-lg-4 col-lg-push-4 col-md-4 col-md-push-4 col-sm-6 col-sm-push-0 col-xs-12 col-xs-push-0">
                <ActionButton buttonText={CREATE_MANUAL_JOB_TITLE} buttonAction={onClick} disabled={!(isValidJob()) || loading} />
            </div>
        </div>
    </>
}

export default UUIFormFieldsView;